import { lazy, type ReactNode } from "react";
import type { RouteObject } from "react-router-dom";
import DashboardRouter from "../../components/DashboardRouter";
import ProtectedRoute from "../../components/auth/ProtectedRoute";
import RoleDashboardRoute from "../../components/auth/RoleDashboardRoute";
import ErrorBoundary from "../../features/shared/pages/ErrorBoundary";
import MainLayout from "../../layouts/MainLayout";
import { APP_ROUTES } from "../routePaths";
import { createProtectedIndexRoute, withSuspense } from "./helpers";

const SuperAdminDashboard = lazy(() => import("../../features/dashboard/superadmin/pages/SuperAdminDashboard"));
const CeoDashboard = lazy(() => import("../../features/ceo-dashboard/pages/CeoDashboard"));
const AdminDashboard = lazy(() => import("../../features/dashboard/pages/AdminDashboard"));
const CustomerServicePage = lazy(
  () => import("../../features/customer-service/pages/CustomerServicePage")
);
const CsJobBoardPage = lazy(() => import("../../features/cs-job-board/pages/CsJobBoardPage"));
const ZbTrackerPage = lazy(() => import("../../features/cs-job-board/pages/ZbTrackerPage"));
const OperationAdminDashboard = lazy(
  () => import("../../features/dashboard/pages/OperationAdminDashboard")
);
const WarehouseDashboard = lazy(() => import("../../features/dashboard/pages/WarehouseDashboard"));
const DriverDashboard = lazy(() => import("../../features/dashboard/pages/DriverDashboard"));
const HRDashboard = lazy(() => import("../../features/dashboard/pages/HRDashboard"));
const HrAdminDashboard = lazy(() => import("../../features/hr-admin-dashboard/pages/HrAdminDashboard"));
// Both sides of the ledger on one screen; ACCOUNTS(900) lands here.
const AccountsDashboard = lazy(() => import("../../features/accounts-dashboard/pages/AccountsDashboard"));
const PayableDashboard = lazy(() => import("../../features/payable-dashboard/pages/PayableDashboard"));
const FleetMaintenanceDashboard = lazy(() => import("../../features/maintenance-dashboard/pages/FleetMaintenanceDashboard"));
const DriverMaintenanceDashboard = lazy(() => import("../../features/driver-maintenance-dashboard/pages/DriverMaintenanceDashboard"));
const EmployeeDashboard = lazy(() => import("../../features/dashboard/pages/EmployeeDashboard"));
const ForwardingDashboard = lazy(() => import("../../features/forwarding-dashboard/pages/ForwardingDashboard"));

interface DashboardRouteConfig {
  path: string;
  requiredRoles: number[];
  element: ReactNode;
}

const createRoleDashboardRoute = ({
  path,
  requiredRoles,
  element,
}: DashboardRouteConfig): RouteObject => ({
  path,
  element: (
    <ProtectedRoute>
      <RoleDashboardRoute requiredRoles={requiredRoles}>
        <MainLayout />
      </RoleDashboardRoute>
    </ProtectedRoute>
  ),
  errorElement: <ErrorBoundary />,
  children: [{ index: true, element: withSuspense(element) }],
});

const dashboardRouteConfigs: DashboardRouteConfig[] = [
  { path: "/dashboard/superadmin", requiredRoles: [100], element: <SuperAdminDashboard /> },
  {
    // Role 100 with permission 1 only; the page and the server both check the permission.
    path: APP_ROUTES.ceoDashboard,
    requiredRoles: [100],
    element: <CeoDashboard />,
  },
  { path: "/dashboard/admin", requiredRoles: [200], element: <AdminDashboard /> },
  {
    path: "/dashboard/customerservicedb",
    requiredRoles: [300],
    element: <CustomerServicePage />,
  },
  {
    path: "/dashboard/customerservice",
    requiredRoles: [100, 200, 300],
    element: <CustomerServicePage />,
  },
  {
    // Someone in the signed-in employee's branch, read only (openspec cs-team-dashboards);
    // the server decides who may be opened.
    path: "/dashboard/customerservice/employee/:employeeId",
    requiredRoles: [100, 200, 300],
    element: <CustomerServicePage />,
  },
  {
    // The CS job board (openspec cs-job-board): my jobs, or someone in my branch's; the server
    // decides who may be opened and who may edit.
    path: "/dashboard/customerservice/board",
    requiredRoles: [100, 200, 300],
    element: <CsJobBoardPage />,
  },
  {
    path: "/dashboard/customerservice/board/employee/:employeeId",
    requiredRoles: [100, 200, 300],
    element: <CsJobBoardPage />,
  },
  {
    // The ZB tracker (openspec cs-job-board): the Excel ZB TRACKER sheets, one list per company.
    path: "/dashboard/customerservice/zb",
    requiredRoles: [100, 200, 300],
    element: <ZbTrackerPage />,
  },
  {
    path: "/dashboard/operationadmin",
    requiredRoles: [400, 500, 600],
    element: <OperationAdminDashboard />,
  },
  { path: "/dashboard/warehouse", requiredRoles: [700], element: <WarehouseDashboard /> },
  // Drivers sign in through the mobile app and have no web role; admins open this from the dashboard picker.
  { path: "/dashboard/driver", requiredRoles: [100, 200], element: <DriverDashboard /> },
  { path: "/dashboard/hr", requiredRoles: [100, 200, 800], element: <HRDashboard /> },
  {
    // Legacy /dashboard/HRAdminDashboard (EmployeeType HRADMIN). This app has no
    // HR admin role, so the HR desk and the admins reach it by URL or the
    // super admin dashboard picker.
    path: "/dashboard/hradmin",
    requiredRoles: [100, 200, 800],
    element: <HrAdminDashboard />,
  },
  {
    path: "/dashboard/accounts",
    requiredRoles: [900, 1100, 1200],
    element: <AccountsDashboard />,
  },
  {
    path: "/dashboard/payable",
    // 1100 owns this desk; 900 (accounts manager) and admin can look in.
    requiredRoles: [100, 200, 900, 1100],
    element: <PayableDashboard />,
  },
  {
    path: "/dashboard/maintenance",
    // 100/200: super admin and admin also see the fleet spending view here.
    requiredRoles: [100, 200, 1300],
    element: <FleetMaintenanceDashboard />,
  },
  {
    path: "/dashboard/driver-maintenance",
    // 1600 owns this desk; super admin and admin can look in.
    requiredRoles: [100, 200, 1600],
    element: <DriverMaintenanceDashboard />,
  },
  {
    path: "/dashboard/employee",
    requiredRoles: [100, 200], 
    element: <EmployeeDashboard />,
  },
  {
    path: "/dashboard/forwarding",
    // The forwarding desk, plus super admin and admin looking in. Legacy sent
    // employees here off EmployeeType == "FORWARDING", which this app does not
    // carry - it routes on role_id, and forwarding staff share role 500 with the
    // operations desk. So 500 keeps landing on /dashboard/operationadmin and this
    // screen is reached by its URL, exactly as the legacy one was: it had no menu
    // entry either.
    requiredRoles: [100, 200, 500],
    element: <ForwardingDashboard />,
  },
];

export const dashboardRoutes: RouteObject[] = [
  createProtectedIndexRoute("/dashboard", <DashboardRouter />),
  ...dashboardRouteConfigs.map(createRoleDashboardRoute),
];
